import { DomUtils } from "./DomUtils.js";

/**
 * Синхронизирует изменения DOM на основе нового рендера компонента
 * Подход: рендерим весь компонент заново и сопоставляем элементы по селектору
 */
export class DomSynchronizer {
	constructor(vroc) {
		this.vroc = vroc;
	}

	/**
	 * Синхронизирует DOM компонента с новым рендером
	 * @param {string} id - ID компонента
	 * @param {HTMLElement} tempContainer - Временный контейнер с новым рендером
	 */
	async syncDomChanges(id, tempContainer) {
		const component = this.vroc.components.get(id);
		if (!component) return;

		const { container } = component;
		this.currentContainer = container; // Сохраняем для использования в _insertNewElement

		console.log(`[DomSynchronizer] ===== Синхронизация DOM для компонента "${id}" =====`);

		// 1. Собираем все элементы с data-xpath из СТАРОГО DOM (ДО изменений)
		const oldElements = this._collectElements(container, 'old');

		// 2. Собираем все элементы с data-xpath из НОВОГО рендера
		const newElements = this._collectElements(tempContainer, 'new');

		console.log(`[DomSynchronizer] Найдено: ${oldElements.length} старых элементов, ${newElements.length} новых элементов`);

		// 3. Группируем по xpath
		const oldByXPath = this._groupByXPath(oldElements);
		const newByXPath = this._groupByXPath(newElements);

		console.log(`[DomSynchronizer] Уникальных xpath: ${oldByXPath.size} старых, ${newByXPath.size} новых`);

		// 4. Обрабатываем каждый xpath
		const processedXPaths = new Set();

		// Сначала обрабатываем xpath, которые есть в новом рендере
		for (const [xpath, newGroup] of newByXPath) {
			const oldGroup = oldByXPath.get(xpath) || [];

			console.log(`[DomSynchronizer] Обработка xpath="${xpath}": ${oldGroup.length} старых → ${newGroup.length} новых`);

			this._syncXPathGroup(xpath, oldGroup, newGroup);
			processedXPaths.add(xpath);
		}

		// Удаляем xpath, которых нет в новом рендере
		for (const [xpath, oldGroup] of oldByXPath) {
			if (!processedXPaths.has(xpath)) {
				console.log(`[DomSynchronizer] Удаление xpath="${xpath}": ${oldGroup.length} элементов`);
				oldGroup.forEach(item => {
					console.log(`[DomSynchronizer]   Удаление: ${item.selector}`);
					item.node.remove();
				});
			}
		}

		console.log(`[DomSynchronizer] ===== Синхронизация завершена =====`);
	}

	/**
	 * Собирает все элементы с data-xpath из контейнера
	 * @param {HTMLElement} container - Контейнер
	 * @param {string} label - Метка для логирования (old/new)
	 * @returns {Array} - Массив объектов {node, xpath, selector, id, index}
	 */
	_collectElements(container, label) {
		const elements = Array.from(container.querySelectorAll('[data-xpath]'));

		return elements.map((node, index) => {
			const xpath = node.getAttribute('data-xpath');
			const selector = DomUtils.generateDomSelector(node);
			const id = node.id || null;
			const text = node.textContent?.substring(0, 40).replace(/\s+/g, ' ').trim();

			return { node, xpath, selector, id, index, text };
		});
	}

	/**
	 * Группирует элементы по xpath
	 * @param {Array} elements - Массив элементов
	 * @returns {Map} - Map: xpath -> Array<element>
	 */
	_groupByXPath(elements) {
		const grouped = new Map();

		elements.forEach(element => {
			if (!grouped.has(element.xpath)) {
				grouped.set(element.xpath, []);
			}
			grouped.get(element.xpath).push(element);
		});

		return grouped;
	}

	/**
	 * Синхронизирует группу элементов с одинаковым xpath
	 * @param {string} xpath - XPath элементов
	 * @param {Array} oldGroup - Старые элементы
	 * @param {Array} newGroup - Новые элементы
	 */
	_syncXPathGroup(xpath, oldGroup, newGroup) {
		// Случай 1: Новых элементов нет - удаляем все старые
		if (newGroup.length === 0) {
			oldGroup.forEach(item => item.node.remove());
			return;
		}

		// Случай 2: Старых элементов нет - вставляем все новые
		if (oldGroup.length === 0) {
			console.log(`[DomSynchronizer]   Вставка ${newGroup.length} новых элементов (старых нет)`);
			newGroup.forEach((item, idx) => {
				console.log(`[DomSynchronizer]   [${idx}/${newGroup.length}] Вставка элемента:`);
				this._insertNewElement(item);
			});
			return;
		}

		// Случай 3: Один к одному
		if (oldGroup.length === 1 && newGroup.length === 1) {
			const oldItem = oldGroup[0];
			const newItem = newGroup[0];

			// ВАЖНО: Проверяем, есть ли дочерние элементы с data-xpath
			const hasChildrenWithXPath = oldItem.node.querySelectorAll('[data-xpath]').length > 0;

			if (hasChildrenWithXPath) {
				// Если у элемента есть дочерние с data-xpath, не заменяем его целиком
				// Дочерние элементы будут обработаны на следующих итерациях
				console.log(`[DomSynchronizer]   Пропуск замены 1:1 (есть дочерние с data-xpath)`);
				console.log(`[DomSynchronizer]     Элемент: ${oldItem.selector} - "${oldItem.text}"`);
				return;
			}

			console.log(`[DomSynchronizer]   Замена 1:1`);
			console.log(`[DomSynchronizer]     Старый: ${oldItem.selector} - "${oldItem.text}"`);
			console.log(`[DomSynchronizer]     Новый:  ${newItem.selector} - "${newItem.text}"`);

			this._replaceElement(oldItem, newItem);
			return;
		}

		// Случай 4: Множественные элементы - сопоставление по селектору
		console.log(`[DomSynchronizer]   Сопоставление N:M (${oldGroup.length}:${newGroup.length})`);
		this._matchAndReplaceGroup(oldGroup, newGroup);
	}

	/**
	 * Вставляет новый элемент в DOM
	 * @param {Object} item - Элемент для вставки
	 */
	_insertNewElement(item) {
		// Ищем родителя по xpath
		const parentXPath = this._getParentXPath(item.xpath);

		console.log(`[DomSynchronizer]     Вставка нового элемента:`);
		console.log(`[DomSynchronizer]       xpath: ${item.xpath}`);
		console.log(`[DomSynchronizer]       selector: ${item.selector}`);
		console.log(`[DomSynchronizer]       parentXPath: ${parentXPath}`);

		if (!parentXPath) {
			console.warn(`[DomSynchronizer]       ❌ Не удалось определить родителя для xpath="${item.xpath}"`);
			return;
		}

		// Ищем родителя сначала в контейнере компонента, потом глобально
		let parent = null;
		if (this.currentContainer) {
			parent = this.currentContainer.querySelector(`[data-xpath="${parentXPath}"]`);
			console.log(`[DomSynchronizer]       parent в контейнере компонента:`, parent ? `✅ ${parent.tagName}.${parent.className}` : '❌ НЕТ');
		}

		if (!parent) {
			parent = document.querySelector(`[data-xpath="${parentXPath}"]`);
			console.log(`[DomSynchronizer]       parent в document:`, parent ? `✅ ${parent.tagName}.${parent.className}` : '❌ НЕТ');
		}

		if (!parent) {
			console.warn(`[DomSynchronizer]       ❌ Родитель не найден для xpath="${parentXPath}"`);
			return;
		}

		const clone = item.node.cloneNode(true);
		clone.removeAttribute('data-diff-marker');
		parent.appendChild(clone);

		console.log(`[DomSynchronizer]       ✅ Вставлен: ${item.selector}`);
	}

	/**
	 * Заменяет один элемент другим
	 * @param {Object} oldItem - Старый элемент
	 * @param {Object} newItem - Новый элемент
	 */
	_replaceElement(oldItem, newItem) {
		if (oldItem.node.isEqualNode(newItem.node)) {
			console.log(`[DomSynchronizer]     Элементы идентичны, пропуск`);
			return;
		}

		const clone = newItem.node.cloneNode(true);
		clone.removeAttribute('data-diff-marker');
		oldItem.node.replaceWith(clone);
	}

	/**
	 * Сопоставляет и заменяет группу элементов
	 * @param {Array} oldGroup - Старые элементы
	 * @param {Array} newGroup - Новые элементы
	 */
	_matchAndReplaceGroup(oldGroup, newGroup) {
		// Группируем по ключу (id или selector)
		const oldByKey = new Map();
		const newByKey = new Map();

		console.log(`[DomSynchronizer]     Старые элементы:`);
		oldGroup.forEach((item, idx) => {
			const key = item.id || item.selector;
			console.log(`[DomSynchronizer]       [${idx}] key="${key}" - "${item.text}"`);

			if (!oldByKey.has(key)) {
				oldByKey.set(key, []);
			}
			oldByKey.get(key).push(item);
		});

		console.log(`[DomSynchronizer]     Новые элементы:`);
		newGroup.forEach((item, idx) => {
			const key = item.id || item.selector;
			console.log(`[DomSynchronizer]       [${idx}] key="${key}" - "${item.text}"`);

			if (!newByKey.has(key)) {
				newByKey.set(key, []);
			}
			newByKey.get(key).push(item);
		});

		// Сопоставляем и заменяем по ключам
		const replacedOldNodes = new Set();

		for (const [key, newItems] of newByKey) {
			const oldItems = oldByKey.get(key) || [];

			console.log(`[DomSynchronizer]     Группа key="${key}": ${oldItems.length} старых → ${newItems.length} новых`);

			// Заменяем существующие
			const minLength = Math.min(oldItems.length, newItems.length);
			for (let i = 0; i < minLength; i++) {
				console.log(`[DomSynchronizer]       Замена [${i}]:`);
				console.log(`[DomSynchronizer]         Старый: ${oldItems[i].selector} - "${oldItems[i].text}"`);
				console.log(`[DomSynchronizer]         Новый:  ${newItems[i].selector} - "${newItems[i].text}"`);

				this._replaceElement(oldItems[i], newItems[i]);
				replacedOldNodes.add(oldItems[i].node);
			}

			// Вставляем дополнительные новые элементы
			if (newItems.length > oldItems.length) {
				console.log(`[DomSynchronizer]       Вставка ${newItems.length - oldItems.length} дополнительных элементов`);

				// Вставляем после последнего замененного элемента этой группы
				const lastOldItem = oldItems[oldItems.length - 1];
				let insertAfter = lastOldItem ? lastOldItem.node : null;

				for (let i = oldItems.length; i < newItems.length; i++) {
					const clone = newItems[i].node.cloneNode(true);
					clone.removeAttribute('data-diff-marker');

					if (insertAfter && insertAfter.parentNode) {
						insertAfter.after(clone);
						insertAfter = clone;
					} else {
						// Если нет точки вставки, добавляем в конец родителя
						this._insertNewElement(newItems[i]);
					}

					console.log(`[DomSynchronizer]         Вставлен [${i}]: ${newItems[i].selector}`);
				}
			}
		}

		// Удаляем старые элементы, которых нет в новых
		for (const [key, oldItems] of oldByKey) {
			const newItems = newByKey.get(key) || [];

			if (newItems.length < oldItems.length) {
				console.log(`[DomSynchronizer]     Удаление ${oldItems.length - newItems.length} лишних элементов из группы key="${key}"`);

				for (let i = newItems.length; i < oldItems.length; i++) {
					console.log(`[DomSynchronizer]       Удаление [${i}]: ${oldItems[i].selector}`);
					oldItems[i].node.remove();
				}
			}
		}

		// Удаляем элементы с ключами, которых больше нет
		for (const [key, oldItems] of oldByKey) {
			if (!newByKey.has(key)) {
				console.log(`[DomSynchronizer]     Удаление всех элементов с key="${key}"`);
				oldItems.forEach(item => {
					if (!replacedOldNodes.has(item.node)) {
						console.log(`[DomSynchronizer]       Удаление: ${item.selector}`);
						item.node.remove();
					}
				});
			}
		}
	}

	/**
	 * Получает родительский xpath
	 * @param {string} xpath - XPath элемента
	 * @returns {string|null} - Родительский xpath или null
	 */
	_getParentXPath(xpath) {
		const lastSlash = xpath.lastIndexOf('/');
		if (lastSlash <= 0) return null;
		return xpath.substring(0, lastSlash);
	}
}
